import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '@/contexts/ThemeContext';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { normalizePlanName } from '@/utils/planName';

// Plays do período de teste (espelha TRIAL_LIMIT em e-video-vsl-be/helpers/plan-limits.js)
const TRIAL_PLAYS = 300;

interface PlanUsageSectionMobileProps {
  onAddPlaysClick: () => void;
}

export const PlanUsageSectionMobile: React.FC<PlanUsageSectionMobileProps> = ({ onAddPlaysClick }) => {
  useLanguage();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  // Busca dados do usuário e plano do contexto de autenticação
  const { user, plan, isLoading } = useCustomAuth();

  // Dados do plano vindo de /auth/me (agora com dados reais do banco)
  const isCanceled = !plan || plan.status === 'canceled';
  const planName = normalizePlanName(plan?.nome) || 'Teste Gratuito';
  const isTrial = plan?.status === 'trialing' || plan?.periodo_teste === true;
  const totalPlays = isTrial ? TRIAL_PLAYS : (plan?.clicks_total || 300);
  // No trial o backend consome `clicks_per_test` (saldo de 300 que decrementa),
  // não `clicks_per_plan` — então `plan.clicks_usados`, derivado do plano, fica
  // 0. Membros não recebem `clicks_per_test` e caem no valor do plano.
  const trialPlaysLeft = user?.clicks_per_test;
  const playsUsed = isTrial && trialPlaysLeft != null
    ? Math.max(TRIAL_PLAYS - trialPlaysLeft, 0)
    : plan?.clicks_usados || 0;
  const percentageUsed = totalPlays > 0 ? Math.round((playsUsed / totalPlays) * 100) : 0;
  const daysUntilRenewal: number = plan?.dias_renovacao || user?.dias_renovacao || 0;

  // Determinar cor da barra baseada no uso
  const getBarColor = () => {
    if (percentageUsed >= 95) return '#EF4444'; // Vermelho intenso
    if (percentageUsed >= 90) return '#F97316'; // Laranja
    return '#1F6FFB'; // Azul HostVSL
  };

  const getBarBgColor = () => {
    if (percentageUsed >= 95) return '#FEE2E2';
    if (percentageUsed >= 90) return '#FFEDD5';
    return '#E6EDFF';
  };

  const getShimmerDuration = () => {
    if (percentageUsed >= 95) return 1.0; // Muito rápido (urgência máxima)
    if (percentageUsed >= 90) return 1.5; // Rápido (urgência)
    if (percentageUsed >= 70) return 2.0; // Moderado
    return 2.5; // Normal
  };

  // Se estiver carregando, mostra skeleton ou loading state
  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{
          background: isDark ? '#151922' : '#F9FAFB',
          border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
          borderRadius: '12px',
          padding: '14px 14px',
          height: '180px'
        }} className="animate-pulse" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Card Compacto do Plano */}
      <div style={{ 
        background: isDark ? '#151922' : '#F9FAFB',
        border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
        borderRadius: '12px',
        padding: '14px 14px'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* Título: Nome do Plano com Upgrade (ou estado "sem plano") */}
          {isCanceled ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: isDark ? '#FFFFFF' : '#111111' }}>
                {translateInterfaceText("Sem plano ativo")}</span>
              <Button
                className="w-full font-semibold"
                style={{
                  height: '40px',
                  borderRadius: '10px',
                  fontSize: '13px',
                  background: '#1F6FFB',
                  color: '#FFFFFF',
                }}
                onClick={() => navigate('/checkout')}
              >
                {translateInterfaceText("Ativar o plano")}</Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              {/* Mesmo rótulo do desktop: normalizePlanName devolve o nome sem
                  o prefixo, e quem exibe o escreve. */}
              <span style={{ fontSize: '13px', fontWeight: 600, color: isDark ? '#FFFFFF' : '#111111' }}>{translateInterfaceText("Plano")}{" "}{planName}</span>
              <button
                style={{ fontSize: '13px', fontWeight: 600, color: '#1F6FFB' }}
                className="transition-colors"
                onClick={() => navigate('/checkout')}
              >
                {translateInterfaceText("Upgrade")}</button>
            </div>
          )}

          {/* Subtítulo: Uso do Plano com números */}
          {!isCanceled && (
          <>
          <div className="flex items-center justify-between">
            <span style={{ fontSize: '12px', fontWeight: 600, color: isDark ? '#94A3B8' : '#64748B' }}>
              {translateInterfaceText("Uso do Plano")}</span>
            <span style={{ fontSize: '12px', fontWeight: 600, color: isDark ? '#94A3B8' : '#64748B', fontVariantNumeric: 'tabular-nums' }}>
              {playsUsed.toLocaleString(getInterfaceLanguage())} / {totalPlays.toLocaleString(getInterfaceLanguage())}
            </span>
          </div>

          {/* Barra de Progresso Premium Slim (6px) */}
          <div className="relative">
            {/* Percentual acima da barra */}
            <div className="flex justify-end mb-1">
              <motion.span 
                className="text-[11px] font-semibold"
                style={{ color: getBarColor() }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 1 }}
              >
                {percentageUsed}%
              </motion.span>
            </div>
            
            {/* Barra slim 6px */}
            <div 
              className="w-full overflow-hidden transition-colors duration-500"
              style={{ height: '6px', backgroundColor: getBarBgColor(), borderRadius: '10px' }}
            >
              <motion.div 
                className="h-full relative overflow-hidden"
                style={{ borderRadius: '10px' }}
                initial={{ width: 0 }}
                animate={{ 
                  width: `${percentageUsed}%`,
                  backgroundColor: getBarColor()
                }}
                transition={{ 
                  width: {
                    duration: 1.2,
                    ease: [0.4, 0, 0.2, 1],
                    delay: 0.2
                  },
                  backgroundColor: {
                    duration: 0.5,
                    ease: "easeInOut"
                  }
                }}
              >
                {/* Shimmer effect overlay */}
                <motion.div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
                    width: '50%'
                  }}
                  initial={{ x: '-100%' }}
                  animate={{ x: '300%' }}
                  transition={{
                    duration: getShimmerDuration(),
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: 1.5
                  }}
                />
              </motion.div>
            </div>
          </div>

          {/* Reinicia em X dias */}
          <div style={{ fontSize: '12px', fontWeight: 500, color: isDark ? '#94A3B8' : '#64748B' }}>
            {translateInterfaceText("Reinicia em")}{" "}{daysUntilRenewal} {daysUntilRenewal === 1 ? translateInterfaceText("dia") : translateInterfaceText("dias")}
          </div>
          </>
          )}
        </div>
      </div>

      {/* CTA Compacto — oculto quando não há plano ativo */}
      {!isCanceled && (
        <Button
          className="w-full font-semibold"
          style={{
            height: '44px',
            borderRadius: '12px',
            fontSize: '15px',
            background: '#1F6FFB',
            color: '#FFFFFF'
          }}
          onClick={onAddPlaysClick}
        >
          {translateInterfaceText("+ Adicionar ao Plano")}</Button>
      )}
    </div>
  );
};
